import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Clock, Users2, Sparkles, Network, ChevronRight, ShieldCheck } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function LandingPage() {
  const { t, lang } = useI18n();
  const benefits = [
    { icon: Globe2, key: "remote_freedom", desc_en: "Work from anywhere with autonomy and full ownership over your day.", desc_hi: "कहीं से भी पूर्ण स्वायत्तता के साथ काम करें।" },
    { icon: Clock, key: "flexible_hours", desc_en: "Choose hours that match your peak focus — output over presence.", desc_hi: "अपने सबसे अच्छे फ़ोकस घंटे चुनें।" },
    { icon: Users2, key: "smart_collab", desc_en: "Async-first communication tools and structured pipelines.", desc_hi: "एसिंक-फ़र्स्ट कम्युनिकेशन टूल्स।" },
    { icon: Sparkles, key: "learn_grow", desc_en: "Built-in trainings, certifications and a real growth path.", desc_hi: "बिल्ट-इन प्रशिक्षण व प्रमाणन।" },
    { icon: Network, key: "connected_community", desc_en: "A global network of operators, freelancers, and digital pros.", desc_hi: "एक वैश्विक नेटवर्क।" },
  ];

  return (
    <div>
      {/* Hero — premium animated, auto-cycling scenes (pure CSS) */}
      <section className="hero relative overflow-hidden bg-[#061228] text-white">
        {/* Scene layers — cross-fade automatically */}
        <div className="absolute inset-0 -z-0">
          <div className="absolute inset-0 hero-grid opacity-50" />
          <div className="hero-scene scene-1" />
          <div className="hero-scene scene-2" />
          <div className="hero-scene scene-3" />
          <div className="hero-scene scene-4" />
          {/* Soft floating orbs */}
          <div className="hero-orb a" />
          <div className="hero-orb b" />
          <div className="hero-orb c" />
          {/* Floating data particles */}
          <div className="hero-particles">
            {Array.from({ length: 36 }).map((_, i) => (
              <span key={`hp-${i}`} style={{ "--i": i }} />
            ))}
          </div>
          {/* Subtle scanlines for "digital workspace" feel */}
          <div className="hero-scan" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#061228]/85 pointer-events-none" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-24 pb-20 sm:pb-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs sm:text-sm fade-up">
            <ShieldCheck size={14} className="text-[var(--gold)]" /> {lang === "hi" ? "हज़ारों पेशेवरों का भरोसा" : "Trusted by thousands of professionals"}
          </div>
          <h1 data-testid="hero-title" className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight max-w-4xl fade-up mt-6 leading-[1.05]">
            {t("hero_title_1")} <span className="hero-brand bg-gradient-to-r from-[var(--gold)] to-amber-200 bg-clip-text text-transparent">Pelico Data</span>.<br />
            {t("hero_title_2")}
          </h1>
          <p className="mt-5 sm:mt-7 max-w-2xl text-zinc-300 text-base sm:text-lg leading-relaxed fade-up">{t("hero_sub")}</p>
          <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 fade-up">
            <Link to="/about" data-testid="learn-more-btn"
              className="cta-pulse inline-flex items-center gap-2 px-5 sm:px-6 py-3 rounded-full bg-white text-[var(--ink)] font-semibold text-sm sm:text-base hover:bg-[var(--accent)] hover:text-white transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-amber-400/30">
              {t("learn_more")} <ArrowRight size={16} />
            </Link>
            <Link to="/login"
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 rounded-full border border-white/25 text-white font-semibold text-sm sm:text-base hover:bg-white hover:text-[var(--ink)] transition-all backdrop-blur">
              {t("sign_in")} →
            </Link>
          </div>

          <div className="mt-14 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8 sm:pt-10">
            {[["10K+", t("active_operators")], ["1,500", t("typing_pages")], ["98.4%", t("avg_accuracy")], ["24/7", t("async_support")]].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl sm:text-4xl font-bold">{n}</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-400 mt-1.5 sm:mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scoped animations */}
        <style>{`
          .hero .hero-scene {
            position: absolute; inset: 0;
            opacity: 0;
            background-size: cover;
            background-position: center;
            animation: scene-cycle 32s ease-in-out infinite;
            will-change: opacity, transform;
            transform: scale(1.08);
          }
          .hero .scene-1 { animation-delay: 0s;  background:
              radial-gradient(circle at 20% 30%, rgba(212,175,55,0.20), transparent 55%),
              radial-gradient(circle at 80% 70%, rgba(37,99,235,0.20), transparent 60%),
              linear-gradient(135deg, #050f24, #0b1f3a); }
          .hero .scene-2 { animation-delay: 8s;  background:
              radial-gradient(circle at 70% 25%, rgba(212,175,55,0.18), transparent 50%),
              radial-gradient(circle at 25% 75%, rgba(13,148,136,0.20), transparent 55%),
              linear-gradient(135deg, #06122a, #0d2347); }
          .hero .scene-3 { animation-delay: 16s; background:
              radial-gradient(circle at 50% 50%, rgba(37,99,235,0.28), transparent 55%),
              radial-gradient(circle at 15% 20%, rgba(212,175,55,0.18), transparent 50%),
              linear-gradient(135deg, #040c1c, #0b1d3a); }
          .hero .scene-4 { animation-delay: 24s; background:
              radial-gradient(circle at 80% 80%, rgba(212,175,55,0.18), transparent 50%),
              radial-gradient(circle at 20% 30%, rgba(59,130,246,0.18), transparent 55%),
              linear-gradient(135deg, #06112a, #0c1e3c); }
          @keyframes scene-cycle {
            0%   { opacity: 0; transform: scale(1.10); }
            6%   { opacity: 1; transform: scale(1.06); }
            22%  { opacity: 1; transform: scale(1.02); }
            28%  { opacity: 0; transform: scale(1.08); }
            100% { opacity: 0; transform: scale(1.10); }
          }

          .hero .hero-orb { position: absolute; border-radius: 9999px; filter: blur(110px); will-change: transform; }
          .hero .hero-orb.a { top: -18%; right: -10%; width: 42rem; height: 42rem; background: rgba(212,175,55,0.16); animation: heroDriftA 14s ease-in-out infinite alternate; }
          .hero .hero-orb.b { bottom: -22%; left: -12%; width: 36rem; height: 36rem; background: rgba(37,99,235,0.22); animation: heroDriftB 20s ease-in-out infinite alternate; }
          .hero .hero-orb.c { top: 35%; left: 40%; width: 20rem; height: 20rem; background: rgba(14,165,233,0.14); animation: heroDriftC 18s ease-in-out infinite alternate; }
          @keyframes heroDriftA { 0% { transform: translate(0,0); } 100% { transform: translate(-70px, 40px); } }
          @keyframes heroDriftB { 0% { transform: translate(0,0); } 100% { transform: translate(60px, -30px); } }
          @keyframes heroDriftC { 0% { transform: translate(0,0) scale(1); } 100% { transform: translate(40px, 30px) scale(1.1); } }

          .hero .hero-particles { position: absolute; inset: 0; pointer-events: none; }
          .hero .hero-particles span {
            position: absolute; width: 3px; height: 3px;
            background: #ffffff; opacity: 0.35; border-radius: 9999px;
            top: calc((var(--i) * 41%) % 100%);
            left: calc((var(--i) * 59%) % 100%);
            animation: heroFloat 22s linear infinite;
            animation-delay: calc(var(--i) * -0.6s);
            box-shadow: 0 0 6px rgba(255,255,255,0.45);
          }
          .hero .hero-particles span:nth-child(4n)   { background: #f2d27a; box-shadow: 0 0 8px rgba(242,210,122,0.55); width: 2px; height: 2px; }
          .hero .hero-particles span:nth-child(7n+1) { background: #93c5fd; box-shadow: 0 0 8px rgba(147,197,253,0.5); }
          @keyframes heroFloat {
            0%   { transform: translateY(0) translateX(0); opacity: 0; }
            10%  { opacity: 0.5; }
            50%  { transform: translateY(-50vh) translateX(18px); opacity: 0.35; }
            90%  { opacity: 0.15; }
            100% { transform: translateY(-100vh) translateX(-12px); opacity: 0; }
          }

          .hero .hero-scan {
            position: absolute; inset: 0; pointer-events: none;
            background: repeating-linear-gradient(180deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 8px);
            mix-blend-mode: overlay; opacity: 0.6;
          }

          /* Pelico Data word gentle breathe */
          .hero .hero-brand {
            display: inline-block;
            animation: brandBreathe 6s ease-in-out infinite;
          }
          @keyframes brandBreathe {
            0%, 100% { filter: drop-shadow(0 0 0 rgba(212,175,55,0)); transform: translateZ(0); }
            50%      { filter: drop-shadow(0 4px 22px rgba(212,175,55,0.35)); }
          }

          /* CTA soft pulse ring */
          .hero .cta-pulse { position: relative; }
          .hero .cta-pulse::before {
            content: ""; position: absolute; inset: -4px; border-radius: 9999px;
            border: 1px solid rgba(212,175,55,0.4); opacity: 0;
            animation: ctaPulse 3.4s ease-out infinite;
          }
          @keyframes ctaPulse {
            0%   { opacity: 0.6; transform: scale(1); }
            70%  { opacity: 0;   transform: scale(1.12); }
            100% { opacity: 0;   transform: scale(1.18); }
          }

          @media (prefers-reduced-motion: reduce) {
            .hero .hero-scene, .hero .hero-orb, .hero .hero-particles span,
            .hero .hero-brand, .hero .cta-pulse::before { animation: none !important; }
            .hero .hero-scene.scene-1 { opacity: 1; }
          }
        `}</style>
      </section>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10 sm:mb-12">
          <div>
            <div className="chip mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" /> {t("why_join")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">{t("why_title")}</h2>
          </div>
          <Link to="/faq" className="text-sm font-semibold inline-flex items-center gap-1 hover:text-[var(--accent)]">{t("read_faq")} <ChevronRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={b.key} data-testid={`benefit-${i}`} className="card-clean p-5 sm:p-6 hover:border-[var(--ink)] hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group">
                <div className="w-11 h-11 rounded-xl bg-[var(--ink)] text-white flex items-center justify-center group-hover:bg-[var(--accent)] transition-colors">
                  <Icon size={20} />
                </div>
                <div className="mt-5 font-display font-bold text-base sm:text-lg">{t(b.key)}</div>
                <div className="mt-2 text-xs sm:text-sm text-zinc-600 leading-relaxed">{lang === "hi" ? b.desc_hi : b.desc_en}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-[var(--ink)] text-white py-5 sm:py-6 overflow-hidden">
        <div className="flex gap-8 sm:gap-12 marquee-track whitespace-nowrap text-xl sm:text-2xl font-display font-semibold tracking-tight">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={`marquee-${i}`} className="flex items-center gap-8 sm:gap-12">
              Type Faster <span className="text-[var(--accent)]">●</span> Earn Smarter
              <span className="text-[var(--accent)]">●</span> Work Anywhere
              <span className="text-[var(--accent)]">●</span>
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
        <div className="card-clean p-8 sm:p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 sm:gap-8 bg-gradient-to-br from-white to-zinc-50">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-xl">{t("cta_title")}</h3>
            <p className="mt-3 text-zinc-600 max-w-xl text-sm sm:text-base">{t("cta_sub")}</p>
          </div>
          <Link to="/login" className="btn-primary shrink-0">{t("sign_in")} <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
